/**
 * Single source of truth for lazy-loaded route chunks.
 *
 * Each loader records its page once imported (`routeLoader`), so `lazyRoute` in
 * App.tsx renders a page the sidebar's hover or idle preload has already fetched
 * without suspending. The module system de-dupes repeat calls.
 */
import { routeLoader, type RouteLoader } from './lazy-route';

export const loadLogin = routeLoader(() => import('../routes/login'));
export const loadSignup = routeLoader(() => import('../routes/signup'));
export const loadForgotPassword = routeLoader(() => import('../routes/forgot-password'));
export const loadResetPassword = routeLoader(() => import('../routes/reset-password'));
export const loadVerifyEmail = routeLoader(() => import('../routes/verify-email'));
export const loadVerifyEmailPending = routeLoader(() => import('../routes/verify-email-pending'));
export const loadUnsubscribe = routeLoader(() => import('../routes/unsubscribe'));
export const loadOnboarding = routeLoader(() => import('../routes/onboarding'));
export const loadActivate = routeLoader(() => import('../routes/activate'));
export const loadSelectAccount = routeLoader(() => import('../routes/select-account'));
export const loadDashboard = routeLoader(() => import('../routes/dashboard'));
export const loadAccount = routeLoader(() => import('../routes/account'));
export const loadClients = routeLoader(() => import('../routes/clients'));
export const loadClientNew = routeLoader(() => import('../routes/client-new'));
export const loadClientEdit = routeLoader(() => import('../routes/client-edit'));
/** The client and matter hub: `/clients/:clientId` and `/matters/:matterId` share this chunk. */
export const loadHub = routeLoader(() => import('../routes/hub/index'));
export const loadActivities = routeLoader(() => import('../routes/activities'));
export const loadInvoices = routeLoader(() => import('../routes/invoices'));
export const loadInvoiceNew = routeLoader(() => import('../routes/invoice-new'));
export const loadInvoiceDetail = routeLoader(() => import('../routes/invoice-detail'));
export const loadLedger = routeLoader(() => import('../routes/ledger'));
export const loadJournalForm = routeLoader(() => import('../routes/ledger/journal-form'));
export const loadLedgerAccountDetail = routeLoader(() => import('../routes/ledger/account-detail'));
export const loadEmails = routeLoader(() => import('../routes/emails'));
export const loadCalendar = routeLoader(() => import('../routes/calendar'));
export const loadOAuthCallback = routeLoader(() => import('../routes/oauth-callback'));
export const loadConnectAuthorize = routeLoader(() => import('../routes/connect-authorize'));
export const loadInvite = routeLoader(() => import('../routes/invite'));
export const loadMemberOnboarding = routeLoader(() => import('../routes/member-onboarding'));
export const loadNotFound = routeLoader(() => import('../routes/not-found'));
export const loadHelp = routeLoader(() => import('../routes/help'));
export const loadHelpArticle = routeLoader(() => import('../routes/help-article'));
export const loadReferral = routeLoader(() => import('../routes/referral'));
export const loadBankAccounts = routeLoader(() => import('../routes/bank-accounts'));
export const loadBankAccountDetail = routeLoader(() => import('../routes/bank-account-detail'));
export const loadBankAccountStatements = routeLoader(() => import('../routes/bank-account-statements'));
export const loadBankAccountStatementDetail = routeLoader(() => import('../routes/bank-account-statement-detail'));
export const loadBankAccountUpload = routeLoader(() => import('../routes/bank-account-upload'));
export const loadClientStatement = routeLoader(() => import('../routes/client-statement'));
export const loadMatterStatement = routeLoader(() => import('../routes/matter-statement'));
export const loadExpenses = routeLoader(() => import('../routes/expenses/index'));
export const loadExpenseNew = routeLoader(() => import('../routes/expenses/expense-new'));
export const loadExpenseDetail = routeLoader(() => import('../routes/expenses/expense-detail'));
export const loadSupplierNew = routeLoader(() => import('../routes/expenses/supplier-new'));
export const loadSupplierEdit = routeLoader(() => import('../routes/expenses/supplier-edit'));
export const loadSupplierDetail = routeLoader(() => import('../routes/expenses/supplier-detail/index'));

/**
 * Sidebar-reachable destinations keyed by path. The sidebar uses this map to
 * prefetch the target route's chunk when a nav link is hovered, focused, or
 * touched - whichever fires first - so the module is typically already in
 * memory by the time the click lands.
 */
export const sidebarRouteLoaders: Record<string, RouteLoader> = {
  '/dashboard': loadDashboard,
  '/clients': loadClients,
  '/activities': loadActivities,
  '/invoices': loadInvoices,
  '/accounting': loadLedger,
  '/emails': loadEmails,
  '/calendar': loadCalendar,
  '/settings': loadAccount,
  '/expenses': loadExpenses,
  '/bank-accounts': loadBankAccounts,
};
